import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom"; // Navigation ke liye
import axiosInstance from "../utils/axiosInstance";

const FullScreenChat = () => {
  const { userId } = useParams(); // URL se userId lein (e.g., /chat/12345)
  const navigate = useNavigate();
  
  // --- States ---
  const [userData, setUserData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  
  // --- Settings States ---
  const [showSettings, setShowSettings] = useState(false);
  const [autoDeleteTime, setAutoDeleteTime] = useState(0); // 0 = Never, values in hours

  // --- Edit/Delete States ---
  const [activeMessageId, setActiveMessageId] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);

  const scrollRef = useRef();
  const fileInputRef = useRef();
  const IMAGE_BASE_URL = axiosInstance.defaults.baseURL || "";

  // =============================================
  // 1. INITIAL LOAD & USER FETCH
  // =============================================
  useEffect(() => {
    if (!userId) return;

    // Fetch User Details
    const fetchUserDetails = async () => {
      try {
        const response = await axiosInstance.get(`/api/chat/user-details/${userId}`);
        if (response.data?.success) {
          setUserData(response.data.data);
        }
      } catch (error) {
        console.error("User fetch error:", error);
      }
    };

    fetchUserDetails();
    fetchMessages();
    markMessagesAsRead(); // ✅ Open karte hi Blue Tick lag jayega

    // Polling for new messages
    const interval = setInterval(() => {
        if(!editingMessage) fetchMessages();
    }, 3000);

    return () => clearInterval(interval);
  }, [userId, editingMessage]);

  // =============================================
  // 2. CORE FUNCTIONS
  // =============================================
  
  const fetchMessages = async () => {
    try {
      const response = await axiosInstance.get(`/api/chat/history/${userId}`);
      if (response.data?.success) {
        setMessages(response.data.data);
      }
    } catch (error) {
      console.error("Message fetch error:", error);
    }
  };

  // ✅ BLUE TICK LOGIC (Backend API call needed)
  const markMessagesAsRead = async () => {
    try {
        // Backend should update 'isRead: true' for this user's messages
        await axiosInstance.put(`/api/chat/mark-read/${userId}`);
    } catch (error) {
        console.error("Read receipt error:", error);
    }
  };

  // ✅ AUTO DELETE LOGIC (Settings Save)
  const saveAutoDeleteSetting = async (hours) => {
      try {
          setAutoDeleteTime(hours);
          // Backend API to save preference
          await axiosInstance.post(`/api/chat/settings/auto-delete`, {
              userId,
              deleteAfterHours: hours
          });
          alert(`Auto-delete set to: ${hours === 0 ? "Never" : hours + " hours"}`);
          setShowSettings(false);
      } catch (error) {
          alert("Failed to save settings");
      }
  };

  // Scroll to bottom
  useEffect(() => {
    if (!editingMessage) {
        scrollRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, editingMessage]);


  // =============================================
  // 📤 SEND / EDIT HANDLER
  // =============================================
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputText && !selectedFile) return;

    if (editingMessage) {
        // Update Logic
        try {
            const response = await axiosInstance.put(`/api/chat/edit/${editingMessage._id}`, {
                message: inputText
            });
            if (response.data?.success) {
                setMessages(prev => prev.map(msg => msg._id === editingMessage._id ? { ...msg, message: inputText } : msg));
                setEditingMessage(null);
                setInputText("");
            }
        } catch (error) { console.error("Edit error", error); }
        return;
    }

    // Send Logic
    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("sender", "admin");
    formData.append("message", inputText || "");
    if (selectedFile) {
      formData.append("image", selectedFile);
      formData.append("type", "image");
    } else {
      formData.append("type", "text");
    }

    try {
      const response = await axiosInstance.post(`/api/chat/send`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data?.success) {
        setMessages(prev => [...prev, response.data.data]);
      }
      setInputText("");
      setSelectedFile(null);
    } catch (error) { console.error("Send error", error); }
  };

  // Delete Handler
  const handleDelete = async (msgId, type) => {
      if(!window.confirm("Are you sure?")) return;
      try {
          await axiosInstance.delete(`/api/chat/delete/${msgId}`, { data: { type } });
          setMessages(prev => prev.filter(m => m._id !== msgId));
      } catch (e) { console.error(e); }
  };

  // =============================================
  // 🎨 STYLES (Full Screen WhatsApp Web Theme)
  // =============================================
  const styles = {
    container: {
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100vw",
        backgroundColor: "#efeae2",
        backgroundImage: "url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')",
        fontFamily: "'Segoe UI', Helvetica, Arial, sans-serif",
        position: "fixed", // Force full screen
        top: 0,
        left: 0,
        zIndex: 9999
    },
    header: {
        height: "60px",
        backgroundColor: "#f0f2f5",
        display: "flex",
        alignItems: "center",
        padding: "0 16px",
        borderBottom: "1px solid #d1d7db",
        justifyContent: "space-between"
    },
    userInfo: { display: "flex", alignItems: "center", cursor: "pointer" },
    avatar: {
        width: "40px",
        height: "40px",
        borderRadius: "50%",
        backgroundColor: "#dfe5e7",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginRight: "15px",
        fontSize: "18px",
        color: "#fff"
    },
    nameText: { fontSize: "16px", fontWeight: "500", color: "#111b21" },
    phoneText: { fontSize: "13px", color: "#667781" },
    
    // Header Actions
    headerActions: { display: "flex", gap: "20px", color: "#54656f", fontSize: "18px" },
    
    // Settings Modal
    settingsModal: {
        position: "absolute",
        top: "60px",
        right: "20px",
        backgroundColor: "#fff",
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        borderRadius: "8px",
        padding: "15px",
        zIndex: 100,
        width: "250px"
    },
    settingItem: { padding: "10px", cursor: "pointer", borderBottom: "1px solid #f0f0f0", fontSize: "14px" },

    // Messages
    chatArea: { flex: 1, overflowY: "auto", padding: "20px 8%" },
    bubble: (sender) => ({
        maxWidth: "60%",
        padding: "6px 7px 8px 9px",
        borderRadius: "7.5px",
        backgroundColor: sender === "admin" ? "#d9fdd3" : "#fff",
        boxShadow: "0 1px 0.5px rgba(11,20,26,0.13)",
        alignSelf: sender === "admin" ? "flex-end" : "flex-start",
        marginBottom: "8px",
        position: "relative",
        fontSize: "14.2px",
        color: "#111b21"
    }),
    row: (sender) => ({ display: "flex", justifyContent: sender === "admin" ? "flex-end" : "flex-start" }),
    
    // Read Receipts
    metaRow: { display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: "2px" },
    time: { fontSize: "11px", color: "#667781", marginRight: "4px" },
    ticks: (isRead) => ({ fontSize: "14px", color: isRead ? "#53bdeb" : "#8696a0" }), // Blue if read

    // Footer
    footer: {
        minHeight: "62px",
        backgroundColor: "#f0f2f5",
        display: "flex",
        alignItems: "center",
        padding: "10px 16px"
    },
    input: {
        flex: 1,
        padding: "12px",
        borderRadius: "8px",
        border: "none",
        fontSize: "15px",
        outline: "none",
        margin: "0 10px"
    },
    iconBtn: { background: "none", border: "none", fontSize: "22px", color: "#54656f", cursor: "pointer", padding: "5px" }
  };

  return (
    <div style={styles.container}>
      {/* --- HEADER --- */}
      <div style={styles.header}>
        <div style={{display:'flex', alignItems:'center'}}>
            <button onClick={() => navigate(-1)} style={{...styles.iconBtn, marginRight: '10px'}}>
                <i className="fa fa-arrow-left"></i>
            </button>
            <div style={styles.userInfo}>
                <div style={styles.avatar}>
                    {userData?.userName?.charAt(0).toUpperCase() || <i className="fa fa-user"></i>}
                </div>
                <div>
                    <div style={styles.nameText}>{userData?.userName || "Loading..."}</div>
                    <div style={styles.phoneText}>{userData?.phone || "+91 XXXXXXXXXX"}</div>
                </div>
            </div>
        </div>
        
        <div style={styles.headerActions}>
            <i className="fa fa-phone" title="Call"></i>
            <i className="fa fa-video" title="Video Call"></i>
            <i 
                className="fa fa-ellipsis-v" 
                title="Settings" 
                style={{cursor: 'pointer'}}
                onClick={() => setShowSettings(!showSettings)}
            ></i>
        </div>
      </div>

      {/* --- SETTINGS DROPDOWN --- */}
      {showSettings && (
          <div style={styles.settingsModal}>
              <div style={{fontWeight: 'bold', marginBottom: '10px', color: '#008069'}}>Auto-Delete Messages</div>
              <div style={styles.settingItem} onClick={() => saveAutoDeleteSetting(0)}>❌ Never Delete</div>
              <div style={styles.settingItem} onClick={() => saveAutoDeleteSetting(1)}>🕐 After 1 Hour</div>
              <div style={styles.settingItem} onClick={() => saveAutoDeleteSetting(24)}>tj After 24 Hours</div>
              <div style={styles.settingItem} onClick={() => saveAutoDeleteSetting(168)}>📅 After 7 Days</div>
              <div style={{fontSize: '11px', color: '#999', marginTop: '10px'}}>
                  Current: {autoDeleteTime === 0 ? "Never" : `${autoDeleteTime} Hours`}
              </div>
          </div>
      )}

      {/* --- CHAT AREA --- */}
      <div style={styles.chatArea}>
        {messages.map((msg, index) => {
            const isRead = msg.isRead || false; // Backend flag
            return (
                <div key={index} style={styles.row(msg.sender)}>
                    <div 
                        style={styles.bubble(msg.sender)}
                        onContextMenu={(e) => {
                            e.preventDefault();
                            if(msg.sender === 'admin') {
                                if(window.confirm("Edit this message?")) {
                                    setEditingMessage(msg);
                                    setInputText(msg.message);
                                }
                            }
                        }}
                    >
                        {msg.messageType === "image" && (
                            <img 
                                src={msg.imageUrl.startsWith("http") ? msg.imageUrl : `${IMAGE_BASE_URL}/${msg.imageUrl}`} 
                                alt="Shared" 
                                style={{maxWidth: '100%', borderRadius: '6px'}}
                            />
                        )}
                        <div>{msg.message}</div>
                        
                        <div style={styles.metaRow}>
                            <span style={styles.time}>
                                {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {/* ✅ BLUE TICK LOGIC */}
                            {msg.sender === "admin" && (
                                <span style={styles.ticks(isRead)}>
                                    <i className={isRead ? "fa fa-check-double" : "fa fa-check"}></i>
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            );
        })}
        <div ref={scrollRef} />
      </div>

      {/* --- FOOTER --- */}
      <form style={styles.footer} onSubmit={handleSendMessage}>
        <input 
            type="file" 
            ref={fileInputRef} 
            onChange={(e) => setSelectedFile(e.target.files[0])}
            style={{display: 'none'}} 
        />
        <button type="button" style={styles.iconBtn} onClick={() => fileInputRef.current.click()}>
            <i className="fa fa-paperclip"></i>
        </button>
        
        <input 
            style={styles.input} 
            placeholder={editingMessage ? "Edit message..." : "Type a message"} 
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
        />
        
        {editingMessage && (
            <button type="button" style={{...styles.iconBtn, color: 'red'}} onClick={() => { setEditingMessage(null); setInputText(""); }}>
                <i className="fa fa-times"></i>
            </button>
        )}

        <button type="submit" style={styles.iconBtn}>
            <i className="fa fa-paper-plane" style={{color: '#008069'}}></i>
        </button>
      </form>
    </div>
  );
};

export default FullScreenChat;